import { add, Currency, Dinero, isNegative, isPositive, isZero, multiply, subtract } from "dinero.js"
import { DateTime } from "luxon"

import { ScaledAmount } from "../../../../../amounts/amountTypes"
import { MonetaryAmount } from "../../../../../currency/currencyTypes"
import { MoneyFactory } from "../../../../../currency/useMoney"
import {
    roundToCurrency,
    roundToNearestFraction,
    toCurrency,
    toDineroLossy,
    toDineroOrUndefined,
} from "../../../../../currency/currencyLib"
import { NumberFactory } from "../../../../../localization/useNumber"
import {
    AdjustmentDirection,
    AdjustmentUnit,
    DetailedPriceList,
    DetailedProductGroups,
    PriceAdjustmentType,
    RoundingMode,
} from "../../priceListTypes"

export enum PriceChangeDirection {
    INCREASE = "INCREASE",
    REDUCTION = "REDUCTION",
    UNCHANGED = "UNCHANGED",
}

export const ROUNDING_FRACTIONS: Record<RoundingMode, number> = {
    [RoundingMode.NONE]: 0,
    [RoundingMode.QUARTER]: 0.25,
    [RoundingMode.HALF]: 0.5,
    [RoundingMode.WHOLE]: 1,
}

/** Prices the rounding hints are illustrated with, in major units. */
const ROUNDING_EXAMPLES: Record<RoundingMode, ReadonlyArray<number>> = {
    [RoundingMode.NONE]: [10.35],
    [RoundingMode.QUARTER]: [10.25, 10.5],
    [RoundingMode.HALF]: [10.5, 11],
    [RoundingMode.WHOLE]: [10, 11],
}

export const ROUNDING_LABEL_KEYS = {
    [RoundingMode.NONE]: "adjust-price-list-rounding-none",
    [RoundingMode.QUARTER]: "adjust-price-list-rounding-quarter",
    [RoundingMode.HALF]: "adjust-price-list-rounding-half",
    [RoundingMode.WHOLE]: "adjust-price-list-rounding-whole",
} as const

export const ROUNDING_SUMMARY_KEYS = {
    [RoundingMode.NONE]: "adjust-price-list-summary-rounding-none",
    [RoundingMode.QUARTER]: "adjust-price-list-summary-rounding-quarter",
    [RoundingMode.HALF]: "adjust-price-list-summary-rounding-half",
    [RoundingMode.WHOLE]: "adjust-price-list-summary-rounding-whole",
} as const

export const MAX_VALUE = 9999

const PERCENTAGE_SCALE = 2

/** "%" or the localized currency symbol, as shown next to the entered value. */
export function getUnitLabel(moneyFactory: MoneyFactory, unit: AdjustmentUnit, currency: Currency<number>) {
    return unit === AdjustmentUnit.AMOUNT ? moneyFactory.getLocalizedCurrencySymbol(currency) : "%"
}

export function toPercentageAmount(value: number): ScaledAmount {
    return { amount: Math.round(value * 10 ** PERCENTAGE_SCALE), scale: PERCENTAGE_SCALE }
}

export function formatAdjustmentValue(
    moneyFactory: MoneyFactory,
    numberFactory: NumberFactory,
    unit: AdjustmentUnit,
    value: number,
    currency: Currency<number>,
) {
    return unit === AdjustmentUnit.AMOUNT
        ? moneyFactory.format(toDineroLossy(value, currency))
        : numberFactory.formatPercentage(toPercentageAmount(value))
}

/** Fluent vars shared by the rounding labels, hints and summary: the step, the currency symbol and example prices. */
export function buildRoundingVars(moneyFactory: MoneyFactory, currency: Currency<number>, mode: RoundingMode) {
    const format = (value: number) => moneyFactory.format(toDineroLossy(value, currency))

    return {
        step: format(ROUNDING_FRACTIONS[mode]),
        currency: moneyFactory.getLocalizedCurrencySymbol(currency),
        ...Object.fromEntries(ROUNDING_EXAMPLES[mode].map((example, index) => [`example${index}`, format(example)])),
    }
}

export type AdjustPricesFormValues = Readonly<{
    adjustmentType: AdjustmentDirection
    unit: AdjustmentUnit
    value: number | ""
    rounding: RoundingMode
    effectiveFrom: DateTime | null
    updateExistingOrders: boolean
    productGroupIds: ReadonlyArray<number>
}>

export type PreviewRow = Readonly<{
    productId: number
    productName: string
    productGroupName: string
    current: Dinero<number>
    next: Dinero<number>
    difference: Dinero<number>
}>

// The factor carries two decimals of its own so a percentage like 2.5 stays exact.
const PERCENTAGE_FACTOR_SCALE = 4

type AdjustmentSettings = Readonly<{
    unit: AdjustmentUnit
    value: number
    adjustmentType: AdjustmentDirection
    rounding: RoundingMode
}>

function applyPercentage(price: Dinero<number>, percentage: number, isIncrease: boolean) {
    const signed = isIncrease ? percentage : -percentage
    const factor = Math.round((100 + signed) * 100)

    return multiply(price, { amount: factor, scale: PERCENTAGE_FACTOR_SCALE })
}

function applyFlatAmount(price: Dinero<number>, value: number, currencyCode: string, isIncrease: boolean) {
    const delta = toDineroLossy(isIncrease ? value : -value, toCurrency(currencyCode))

    return add(price, delta)
}

export function calculateAdjustedPrice(
    price: Dinero<number>,
    currencyCode: string,
    { unit, value, adjustmentType, rounding }: AdjustmentSettings,
) {
    const isIncrease = adjustmentType === PriceAdjustmentType.INCREASE

    const adjusted =
        unit === AdjustmentUnit.PERCENTAGE
            ? applyPercentage(price, value, isIncrease)
            : applyFlatAmount(price, value, currencyCode, isIncrease)

    const fraction = ROUNDING_FRACTIONS[rounding]

    return fraction > 0 ? roundToNearestFraction(adjusted, fraction) : roundToCurrency(adjusted)
}

function priceOf(product: DetailedProductGroups["products"][number]): MonetaryAmount | undefined {
    return product.adjustedPrice ?? product.price
}

export function getPriceListCurrency(priceList: DetailedPriceList) {
    const code =
        priceList.priceSetup?.currencyCode ??
        priceList.productGroups
            .flatMap((group) => group.products)
            .map(priceOf)
            .find(Boolean)?.currency

    return code ? toCurrency(code) : undefined
}

type BuildPreviewArgs = Readonly<{
    productGroups: ReadonlyArray<DetailedProductGroups>
    selectedGroupIds: ReadonlyArray<number>
    settings: AdjustmentSettings
}>

export function buildPreviewRows({
    productGroups,
    selectedGroupIds,
    settings,
}: BuildPreviewArgs): ReadonlyArray<PreviewRow> {
    const selected = new Set(selectedGroupIds)

    return productGroups
        .filter((group) => selected.has(group.id))
        .flatMap((group) =>
            group.products.flatMap((product) => {
                const price = priceOf(product)
                const current = toDineroOrUndefined(price)
                if (!price || !current) return []

                const next = calculateAdjustedPrice(current, price.currency, settings)

                return [
                    {
                        productId: product.id,
                        productName: product.name,
                        productGroupName: group.name,
                        current,
                        next,
                        difference: subtract(next, current),
                    },
                ]
            }),
        )
}

function hasPrice(product: DetailedProductGroups["products"][number]) {
    return Boolean(priceOf(product))
}

export function countProductsInGroups(
    productGroups: ReadonlyArray<DetailedProductGroups>,
    selectedGroupIds: ReadonlyArray<number>,
) {
    const selected = new Set(selectedGroupIds)

    return productGroups.reduce(
        (total, group) => (selected.has(group.id) ? total + group.products.filter(hasPrice).length : total),
        0,
    )
}

// Rounding can land a price back on its current value; such a row is shown but is not a change.
export function hasPriceChange(row: PreviewRow) {
    return !isZero(row.difference)
}

/** The row to illustrate the adjustment with: the first that changes, else the first there is. */
export function findExampleRow(rows: ReadonlyArray<PreviewRow>) {
    return rows.find(hasPriceChange) ?? rows[0]
}

export function isNegativeRow(row: PreviewRow) {
    return isNegative(row.next)
}

export function hasNegativePrice(rows: ReadonlyArray<PreviewRow>) {
    return rows.some(isNegativeRow)
}

export function isIncreaseRow(row: PreviewRow) {
    return isPositive(row.difference) && !isZero(row.difference)
}

export function getPriceChangeDirection(row: PreviewRow) {
    if (!hasPriceChange(row)) return PriceChangeDirection.UNCHANGED

    return isIncreaseRow(row) ? PriceChangeDirection.INCREASE : PriceChangeDirection.REDUCTION
}

export function countPricedProductsInList(productGroups: ReadonlyArray<DetailedProductGroups>) {
    return productGroups.reduce((total, group) => total + countPricedProducts(group), 0)
}

export function countPricedProducts(group: DetailedProductGroups) {
    return group.products.filter(hasPrice).length
}
